import React from "react";
import Icon from "@mdi/react";
import {mdiHelpCircleOutline} from "@mdi/js";
import {OverlayTrigger, Tooltip} from "react-bootstrap";

/**
 * Kleines Fragezeichen-Symbol, das beim Hover (oder Fokus) einen erklärenden
 * Tooltip einblendet. Gedacht für Überschriften, Labels oder Funktionen, um
 * Kontext, Regeln oder Verhalten eines Bereichs zu dokumentieren.
 *
 * Bewusst generisch gehalten, damit es überall im Backend wiederverwendet
 * werden kann.
 *
 * @param {React.ReactNode} children - Inhalt des Tooltips (Text oder JSX).
 * @param {string} [placement="right"] - Platzierung des Overlays (Bootstrap).
 * @param {number} [size=0.85] - Icon-Größe in @mdi/react-Einheiten (~rem).
 * @param {string} [id="help-tooltip"] - Optionale ID des Tooltips (a11y).
 * @param {string} [className=""] - Zusätzliche Klassen für das Trigger-Element.
 * @param {string} [iconPath=mdiHelpCircleOutline] - MDI-Pfad für ein gleich gestaltetes Symbol.
 * @param {string} [ariaLabel="Hilfe"] - Zugängliche Bezeichnung des Triggers.
 * @param {Function} [onClick] - Optionale Aktion beim Klick bzw. per Tastatur.
 */
export default function HelpTooltip({
    children,
    placement = "right",
    size = 0.85,
    id = "help-tooltip",
    className = "",
    iconPath = mdiHelpCircleOutline,
    ariaLabel = "Hilfe",
    onClick,
}) {
    const handleKeyDown = (event) => {
        if (!onClick || (event.key !== "Enter" && event.key !== " ")) {
            return;
        }

        event.preventDefault();
        onClick(event);
    };

    return (
        <OverlayTrigger
            placement={placement}
            flip
            popperConfig={{
                modifiers: [
                    {name: "flip", options: {fallbackPlacements: ["bottom", "top", "right", "left"], padding: 8}},
                    {name: "preventOverflow", options: {padding: 8}},
                ],
            }}
            overlay={
                <Tooltip id={id} className="help-tooltip">
                    {children}
                </Tooltip>
            }
        >
            <span
                role="button"
                tabIndex={0}
                aria-label={ariaLabel}
                className={`help-tooltip-trigger d-inline-flex align-items-center text-muted${className ? " " + className : ""}`}
                style={{cursor: onClick ? "pointer" : "help"}}
                onClick={onClick}
                onKeyDown={handleKeyDown}
            >
                <Icon path={iconPath} size={size}/>
            </span>
        </OverlayTrigger>
    );
}
